import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

import { DropdownOptionModel } from './typings/dropdown-option';

// placement: option row height, panel height cap and floor, gap to the nearest scrolling ancestor
const OPTION_HEIGHT    : number = 44;
const PANEL_MARGIN     : number = 10;
const PANEL_MAX_HEIGHT : number = 320;
const PANEL_MIN_HEIGHT : number = 100;

// A stylable select as a stacked field: arrows move, Enter or Space choose, Escape or Tab close
@Component({
  selector   : 'wor-dropdown',
  template: require('./dropdown.component.html')
})
export class DropdownComponent {
  @Input() isDisabled  : boolean                    = false;
  @Input() label       : string                     = '';
  @Input() name        : string                     = '';
  @Input() options     : Array<DropdownOptionModel> = [];
  @Input() placeholder : string                     = '';
  @Input() value       : string | number            = null;

  @Output() valueChange : EventEmitter<string | number> = new EventEmitter<string | number>();

  public activeIndex    : number  = -1;
  public isDropUp       : boolean = false;
  public isOpen         : boolean = false;
  public panelMaxHeight : number  = PANEL_MAX_HEIGHT;

  constructor (
    private elementRef : ElementRef<HTMLElement>
  ) {}

  get selectedLabel () : string {
    const selected = ( this.options || [] ).find(option => option.value === this.value);

    return selected
      ? selected.label
      : '';
  }

  @HostListener('document:click', [ '$event' ])
  onDocumentClick ( event : MouseEvent ) : void {
    if ( this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node) ) {
      this.isOpen = false;
    }
  }

  onTriggerKeydown ( event : KeyboardEvent ) : void {
    if ( this.isDisabled ) {
      return;
    }

    switch ( event.key ) {
      case 'ArrowDown':
        event.preventDefault();

        if ( this.isOpen ) {
          this.moveActive(1);
        } else {
          this.toggle();
        }

        break;
      case 'ArrowUp':
        event.preventDefault();

        if ( this.isOpen ) {
          this.moveActive(-1);
        }

        break;
      case 'Enter':
      case ' ':
        event.preventDefault();

        if ( this.isOpen && this.options[ this.activeIndex ] ) {
          this.select(this.options[ this.activeIndex ]);
        } else {
          this.toggle();
        }

        break;
      case 'Escape':
        // an open list takes the Escape, so a surrounding modal stays open
        if ( this.isOpen ) {
          event.stopPropagation();
        }

        this.isOpen = false;

        break;
      case 'Tab':
        this.isOpen = false;

        break;
    }
  }

  select ( option : DropdownOptionModel ) : void {
    if ( option.isDisabled ) {
      return;
    }

    this.isOpen = false;

    this.focusTrigger();

    if ( option.value === this.value ) {
      return;
    }

    this.value = option.value;

    this.valueChange.emit(option.value);
  }

  // opening lands the highlight on the current choice
  toggle () : void {
    if ( this.isDisabled ) {
      return;
    }

    this.isOpen = !this.isOpen;

    if ( this.isOpen ) {
      this.activeIndex = this.options.findIndex(option => option.value === this.value);

      this.placePanel();
      this.keepActiveInView();
    }
  }

  trackOption ( index : number, option : DropdownOptionModel ) : string | number {
    return option.value;
  }

  // the chosen option's button leaves the page with the list, so focus returns to the trigger
  private focusTrigger () : void {
    const trigger : HTMLElement = this.elementRef.nativeElement.querySelector('.dropdown-toggle');

    if ( trigger ) {
      trigger.focus();
    }
  }

  // the panel exists after the next paint, so the scroll waits a tick
  private keepActiveInView () : void {
    setTimeout(() => {
      const active = this.elementRef.nativeElement.querySelector('.dropdown-item.active');

      if ( active ) {
        active.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // steps over disabled options and stops at the ends rather than wrapping
  private moveActive ( direction : number ) : void {
    let index = this.activeIndex + direction;

    while ( this.options[ index ] && this.options[ index ].isDisabled ) {
      index += direction;
    }

    if ( this.options[ index ] ) {
      this.activeIndex = index;

      this.keepActiveInView();
    }
  }

  // opens on the side with room in the nearest scrolling ancestor, capped so the list scrolls
  private placePanel () : void {
    const trigger = this.elementRef.nativeElement.querySelector('.dropdown-toggle');

    if ( !trigger ) {
      return;
    }

    const rect     = trigger.getBoundingClientRect();
    let clipBottom = window.innerHeight;
    let clipTop    = 0;

    for ( let node = this.elementRef.nativeElement.parentElement; node; node = node.parentElement ) {
      const overflow = getComputedStyle(node).overflowY;

      if ( overflow === 'auto' || overflow === 'scroll' ) {
        const box = node.getBoundingClientRect();

        clipBottom = Math.min(clipBottom, box.bottom);
        clipTop    = Math.max(clipTop, box.top);

        break;
      }
    }

    const needed = Math.min(this.options.length * OPTION_HEIGHT + PANEL_MARGIN, PANEL_MAX_HEIGHT);
    const below  = clipBottom - rect.bottom - PANEL_MARGIN;
    const above  = rect.top - clipTop - PANEL_MARGIN;

    this.isDropUp = below < needed && above > below;

    this.panelMaxHeight = Math.min(PANEL_MAX_HEIGHT, Math.max(this.isDropUp ? above : below, PANEL_MIN_HEIGHT));
  }
}
